import { createContext, useContext, type ReactNode } from "react";
import { createPortal } from "react-dom";

/** Library children place their existing creation actions in the shared heading. */
export const PageHeaderActionsContext = createContext<
  HTMLElement | null | undefined
>(undefined);
import { WorkspaceHeaderContext } from "./WorkspaceHeader";
import { HelpLink } from "@/features/docs/HelpLink";

/** Page-owned title/help/actions. In a workspace, portal into the compact shared
 * header. Library children keep their dedicated action target; outside either
 * context the original standalone heading remains available. */

export type PageHeaderProps = {
  /** Descriptive title — e.g. "Review Playlists", not "Playlists". */
  title: string;
  /**
   * Optional mono UPPERCASE eyebrow above the title — the editorial
   * structural label (e.g. "RIVERSIDE FC · SPRING 2026"). Rendered via
   * the shared `.eyebrow` recipe.
   */
  eyebrow?: ReactNode;
  /** One-line explanation of what this tab is for. */
  description: ReactNode;
  /** Optional action buttons, right-aligned on the same row. */
  actions?: ReactNode;
};

export function PageHeader({
  title,
  eyebrow,
  description,
  actions,
}: PageHeaderProps) {
  const actionTarget = useContext(PageHeaderActionsContext);
  const workspace = useContext(WorkspaceHeaderContext);
  if (actionTarget !== undefined) {
    return actionTarget && actions ? createPortal(actions, actionTarget) : null;
  }
  if (workspace) {
    return (
      <>
        {workspace.heading ? createPortal(
          <h1 className="flex min-w-0 items-center gap-2" data-testid="coach-page-header">
            {title}<HelpLink className="size-7 coach-heading-help" />
          </h1>, workspace.heading) : null}
        {workspace.actions && actions ? createPortal(actions, workspace.actions) : null}
      </>
    );
  }
  return (
    <div
      className="grid min-w-0 gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-start"
      data-testid="coach-page-header"
    >
      <div className="min-w-0 coach-page-heading-copy">
        {eyebrow ? (
          <p className="eyebrow mb-1" data-testid="coach-page-header-eyebrow">
            {eyebrow}
          </p>
        ) : null}
        <h2 className="flex items-center gap-1 font-display text-xl tracking-display text-ink">
          {title}
          <HelpLink className="size-7 coach-heading-help" />
        </h2>
        <p className="mt-0.5 max-w-3xl text-sm leading-snug text-ink-muted">
          {description}
        </p>
      </div>
      {actions ? (
        <div className="min-w-0" data-testid="coach-page-header-actions">
          <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
            {actions}
          </div>
        </div>
      ) : null}
    </div>
  );
}
